import { api, getApiErrorMessage } from './api';
import { getFirebaseAuth, getIdToken, watchAuth } from '../auth/firebase';

const AUTH_WAIT_MS = 1500;
const RETRYABLE_AUTH_DETAILS = /missing bearer token|invalid token|token expired|id token has expired/i;

function mergeAuthHeaders(headers, token) {
  const merged = { ...(headers || {}) };
  if (token) {
    merged.Authorization = `Bearer ${token}`;
  }
  return merged;
}

async function waitForCurrentUser(timeoutMs = AUTH_WAIT_MS) {
  const auth = getFirebaseAuth();
  if (auth.currentUser) {
    return auth.currentUser;
  }
  return new Promise((resolve) => {
    let settled = false;
    let unsubscribe = null;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      resolve(auth.currentUser || null);
    }, timeoutMs);
    unsubscribe = watchAuth((user) => {
      if (settled) return;
      if (!user) return;
      settled = true;
      window.clearTimeout(timer);
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      resolve(user);
    });
  });
}

async function resolveBearerToken(forceRefresh = false) {
  let token = await getIdToken(forceRefresh);
  if (token) {
    return token;
  }
  await waitForCurrentUser();
  token = await getIdToken(forceRefresh);
  return token;
}

function shouldRetryWithRefreshedToken(error) {
  const status = error?.response?.status;
  if (status !== 401) {
    return false;
  }
  const detail = String(error?.response?.data?.detail || '');
  return RETRYABLE_AUTH_DETAILS.test(detail);
}

export async function authenticatedRequest(config, { retry = true } = {}) {
  const initialToken = await resolveBearerToken(false);
  try {
    return await api.request({
      ...config,
      headers: mergeAuthHeaders(config?.headers, initialToken),
    });
  } catch (error) {
    if (!retry || !shouldRetryWithRefreshedToken(error)) {
      throw error;
    }
    const refreshedToken = await resolveBearerToken(true);
    if (!refreshedToken || refreshedToken === initialToken) {
      throw error;
    }
    return api.request({
      ...config,
      headers: mergeAuthHeaders(config?.headers, refreshedToken),
    });
  }
}

export const authenticatedApi = {
  get(url, config = {}) {
    return authenticatedRequest({ ...config, method: 'get', url });
  },
  post(url, data, config = {}) {
    return authenticatedRequest({ ...config, method: 'post', url, data });
  },
  patch(url, data, config = {}) {
    return authenticatedRequest({ ...config, method: 'patch', url, data });
  },
};

export function getAuthAwareErrorMessage(error, fallback = 'Request failed') {
  const status = error?.response?.status;
  const detail = String(error?.response?.data?.detail || '').trim();
  if (status === 401) {
    if (/default firebase app does not exist|initialize_app/i.test(detail)) {
      // Backend Firebase Admin SDK did not initialize. Operators should not
      // see the raw SDK error — surface a clear, actionable message instead.
      return 'Authentication service is starting up. Please retry in a moment.';
    }
    if (/missing bearer token/i.test(detail)) {
      return 'Authentication token was not ready. Please retry.';
    }
    if (/invalid token|token expired|id token has expired/i.test(detail)) {
      return 'Session token expired. Please sign in again.';
    }
    return 'Authentication failed. Please sign in again.';
  }
  if (status === 403) {
    return 'Signed in, but this account is not authorized for this internal page.';
  }
  return getApiErrorMessage(error, fallback);
}
